/*@flow*/
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import useCachedQuery from 'lib/useCachedQuery';
import * as I from 'immutable';
import GetAssociatedResources from './queries/GetAssociatedResources';
import { useState } from 'react';
import useCompanyId from './useCompanyId';
import useContextState from './useContextState';
import inIframe from 'lib/inIframe';
import useBuilderCommands from 'comps/ResourceBuilder/useCommands';
import isEmbedded from 'lib/isEmbedded';
import useStoreState from 'lib/useStoreState';
import PATHS from 'const/paths';

function LTAssociatedViewItem(props) {
  const isIframeEmbedded = inIframe();
  if (isIframeEmbedded) return null;

  const onClick = () => {
    window.open('/page/' + props.hashid);
  };

  return (
    <div
      testid="LTAssociatedViewItem"
      onClick={onClick}
      className="mb-4"
      css={css(`
          border: solid 1px #ccc;
          border-radius: 8px;
          padding: 1rem;
          cursor: pointer;
        `)}
    >
      <div
        className="text-muted mb-2"
        css={css('font-size: 0.9rem; text-transform: uppercase;')}
      >
        {props.resourceType}
      </div>
      <div className="mb-2" css={css('font-size: 1.4rem; font-weight: bold;')}>
        {props.name}
      </div>
      <div className="d-flex justify-content-between">
        <span>{props.category}</span> <span>{props.status}</span>
      </div>
    </div>
  );
}

function LTAssociatedContactsViewItem(props) {
  if (isEmbedded()) return null;

  const onClick = () => {
    window.open("/page/" + props.hashid);
  };

  return (
    <div
      testid="LTAssociatedViewItem"
      onClick={onClick}
      className="mb-4"
      css={css(`
          border: solid 1px #ccc;
          border-radius: 8px;
          cursor: pointer;
          max-width: 330px;
          padding: 1rem;
          width: 100%;
        `)}
    >
      <div className="d-flex justify-content-between">
        <div
          css={css(`
          width: 60%;
        `)}
        >
          <div
            className="text-muted mb-2"
            css={css("font-size: 0.9rem; text-transform: uppercase;")}
          >
            {props.resourceType}
          </div>
          <div
            className="mb-2"
            css={css(`
              font-size: 1.4rem;
              font-weight: bold;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              width: 90%;
            `)}
          >
            {props.name}
          </div>
          <div className="mb-2" css={css("font-size: 1.2rem;")}>
            {props.contactType}
          </div>
        </div>
        <div
          css={css(`
          width: 40%;
        `)}
        >
          <div
            className="text-muted mb-2"
            css={css("font-size: 0.9rem; text-transform: uppercase;")}
          >
            {props.resourceType === "Event" ? (
              props.recurrences && props.recurrences.length > 0 ? (
                <div>
                  {(() => {
                    const schedule = props.recurrences[0];
                    const startDate = new Date(schedule.dtstart);
                    const formattedStart = startDate.toLocaleString("en-US", {
                      year: "numeric",
                      month: "numeric",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                    return <span>{formattedStart}</span>;
                  })()}
                </div>
              ) : (
                <div>-</div>
              )
            ) : (
              <div>
                {`Status: ${props.status}`}
              </div>
            )}
          </div>

          <div className="mb-2" css={css("font-size: 1.2rem;")}>
            {props.resourceType === "Event" ? (
              <div>
                {props.category}
              </div>
            ) : (
              <div>
                {props.phone}
              </div>
            )}
          </div>

          <div
            className="mb-2"
            css={css(`
              font-size: 1.2rem;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              width: 90%;
            `)}
          >
            {props.email}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LTAssociatedView() {
  const ROW_COUNT = 10;
  const [before] = useState(null);
  const [after] = useState(null);
  const [first] = useState(ROW_COUNT);
  const [last] = useState(null);

  const s = useContextState();
  const focusedResourceId = s.focusedResourceId;
  const focusedResourceType = s.focusedResourceType;
  const id = focusedResourceId;
  const c1 = useCompanyId();
  const companyId = window.assetCompanyId || c1;
  const cmdBuilder = useBuilderCommands();

  const { projectRefetchHash } = useStoreState({
    projectRefetchHash: PATHS.projectRefetchHash,
  });

  const [projectRefreshed, setProjectRefreshed] = useState(
    projectRefetchHash
  );

  function onEdit() {
    if (!focusedResourceId) return;
    if (!focusedResourceType) return;

    cmdBuilder.editResource({
      id: focusedResourceId,
      resourceName: focusedResourceType,
    });
  }

  const variables = {
    id,
    companyId,
    resourceName: focusedResourceType,
    first,
    last,
    after,
    before,
    order: 'updated_at',
    orderDir: 'asc',
  };
  const queryOptions = {
    variables,
    fetchPolicy: 'network-only',
  };
  
  const res = useCachedQuery(GetAssociatedResources, queryOptions);
  const { data, loading, error, refetch } = res;

  if (loading) return null;
  if (error) {
    console.error(error);
    return null;
  }

  // Refetch the data when the record is updated
  if (
    projectRefetchHash !== undefined &&
    projectRefreshed !== projectRefetchHash
  ) {
    refetch();
  }

  const associatedResourceTypes = ['contacts', 'tenants', 'events']

  let items = [];
  associatedResourceTypes.forEach((resourceType) => {
    const nodes = I.getIn(
      data,
      ["company", "resource", resourceType, "nodes"],
      []
    );
    items.push(...nodes);
  });

  return (
    <div data-testid='LTAssociatedView' className='mt-4 border-top pt-2'>
      <div className='d-flex align-items-center justify-content-between'>
        <div className='d-flex align-items-center'>
          <h4
            css={css(`
              margin-top: 1rem;
              margin-bottom: 1rem;
              margin-right: 5px;
            `)}
          >
            Associated records
          </h4>
          <span
            className="text-muted"
            css={css(`
              font-size: 16px;
            `)}
          >
            {` (${items.length})`}
          </span>
        </div>
        {!isEmbedded() && 
          <button className='text-link editResourceBtn pl-0' onClick={onEdit}>
            <i className='fa fa-edit' /> Manage
          </button>
        }
      </div>
      {items.length > 0 &&
        items.map((item, i) =>
          item.resourceType === "Tenant" ? (
            <LTAssociatedViewItem key={i} {...item} />
          ) : (
            <LTAssociatedContactsViewItem key={i} {...item} />
          )
        )}
    </div>
  );
}
